import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tooltip } from '../components/ui/Tooltip';
import { FeatherCalendar, FeatherUsers, FeatherRefreshCw, FeatherDownload } from '../components/ui/icons';
import { formatDateForDisplay, formatDateForComparison } from '../utils/date';
import { managerApi } from '../services/api';
import './TimeCapsule.css';

export default function TimeCapsule() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [timeCapsuleData, setTimeCapsuleData] = useState([]);
  const [uniqueDates, setUniqueDates] = useState([]);
  const [taskConnections, setTaskConnections] = useState({});
  const [hoveredTaskId, setHoveredTaskId] = useState(null);

  // Initialize with current week
  useEffect(() => {
    const today = new Date();
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, Monday = 1
    startOfWeek.setDate(today.getDate() - daysToSubtract-7);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    setDateRange({
      startDate: startOfWeek.toISOString().split('T')[0],
      endDate: endOfWeek.toISOString().split('T')[0]
    });
  }, []);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch time capsule data when date range or selected users change
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate && selectedUsers.length > 0) {
      fetchTimeCapsuleData();
    }
  }, [dateRange, selectedUsers]);

  // Calculate task connections when data changes
  useEffect(() => {
    if (Object.keys(timeCapsuleData).length > 0 && uniqueDates.length > 0) {
      const connections = calculateTaskConnections();
      setTaskConnections(connections);
    }
  }, [timeCapsuleData, uniqueDates]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await managerApi.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeCapsuleData = async () => {
    try {
      setLoading(true);
      
      const promises = selectedUsers.map(userId => 
        managerApi.getTimeCapsuleData(userId, dateRange.startDate, dateRange.endDate)
      );

      const results = await Promise.all(promises);
      
      // Process and organize the data
      const processedData = processTimeCapsuleData(results);
      setTimeCapsuleData(processedData);
      
      // Extract unique dates
      const dates = new Set();
      results.forEach(userData => {
        userData.workPlans?.forEach(plan => {
          dates.add(formatDateForComparison(plan.created_at));
        });
      });
      
      const sortedDates = Array.from(dates).sort();
      setUniqueDates(sortedDates);
      
    } catch (error) {
      console.error('Error fetching time capsule data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processTimeCapsuleData = (results) => {
    const processed = {};
    
    results.forEach((userData, index) => {
      const userId = selectedUsers[index];
      processed[userId] = {
        user: userData.user,
        workPlans: userData.workPlans
      };
    });
    
    return processed;
  };

  const calculateTaskConnections = () => {
    const connections = {};
    
    selectedUsers.forEach(userId => {
      const userData = timeCapsuleData[userId];
      if (!userData) return;

      // Group tasks by task_id across all dates
      const taskGroups = {};
      
      userData.workPlans.forEach(plan => {
        const date = formatDateForComparison(plan.created_at);
        plan.work_plan_tasks.forEach(task => {
          const taskId = task.task_id;
          if (!taskGroups[taskId]) {
            taskGroups[taskId] = [];
          }
          taskGroups[taskId].push({
            date,
            task,
            userId
          });
        });
      });

      // Create connections for tasks that appear on multiple dates
      Object.entries(taskGroups).forEach(([taskId, taskGroup]) => {
        if (taskGroup.length > 1) {
          // Sort by date
          taskGroup.sort((a, b) => new Date(a.date) - new Date(b.date));
          
          connections[taskId] = {
            taskTitle: taskGroup[0].task.tasks?.title,
            instances: taskGroup.map(t => ({
              date: t.date,
              taskId: t.task.id,
              hours: t.task.hours || 0,
              userId: t.userId
            })),
            totalHours: taskGroup.reduce((sum, t) => sum + (t.task.hours || 0), 0),
            dateCount: taskGroup.length
          };
        }
      });
    });
    
    return connections;
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const getTasksForUserAndDate = (userId, date) => {
    const userData = timeCapsuleData[userId];
    if (!userData) return [];

    const workPlan = userData.workPlans.find(plan => 
      formatDateForComparison(plan.created_at) === date
    );

    return workPlan?.work_plan_tasks || [];
  };

  const getTaskConnectionInfo = (taskId) => {
    return Object.values(taskConnections).find(conn => 
      conn.instances.some(instance => instance.taskId === taskId)
    );
  };

  const getTaskDayNumber = (taskId) => {
    const connection = getTaskConnectionInfo(taskId);
    if (!connection) return null;
    
    // Sort instances by date to get the correct order
    const sortedInstances = [...connection.instances].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    // Find the index of this task in the sorted list
    const dayIndex = sortedInstances.findIndex(instance => instance.taskId === taskId);
    return dayIndex + 1; // Convert to 1-based indexing
  };

  const getDayLabel = (dayNumber) => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const lastDigit = dayNumber % 10;
    const lastTwoDigits = dayNumber % 100;
    
    // Special cases for 11, 12, 13
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return `${dayNumber}th`;
    }
    
    // Use appropriate suffix based on last digit
    const suffix = lastDigit >= 1 && lastDigit <= 3 ? suffixes[lastDigit] : suffixes[0];
    return `${dayNumber}${suffix}`;
  };

  const isTaskConnected = (taskId) => {
    return getTaskConnectionInfo(taskId) !== undefined;
  };

  const getConnectedTaskIds = (taskId) => {
    const connection = getTaskConnectionInfo(taskId);
    if (!connection) return [];
    return connection.instances.map(instance => instance.taskId);
  };

  const isTaskHighlighted = (taskId) => {
    if (hoveredTaskId === taskId) return true;
    if (hoveredTaskId && getConnectedTaskIds(hoveredTaskId).includes(taskId)) return true;
    return false;
  };

  const handleTaskHover = (taskId) => {
    setHoveredTaskId(taskId);
  };

  const handleTaskLeave = () => {
    setHoveredTaskId(null);
  };

  const getTaskTooltipContent = (task) => {
    const connection = getTaskConnectionInfo(task.id);
    const baseContent = `${task.tasks?.description || 'No description'}\n\nHours: ${task.hours || 0}h\nStatus: ${task.status || 'Unknown'}\nNotes: ${task.notes || 'No notes'}`;
    
    if (connection) {
      const dates = connection.instances.map(instance => formatDateForDisplay(instance.date)).join(', ');
      return `${baseContent}\n\n📅 Multi-day Task:\n• Spans ${connection.dateCount} day${connection.dateCount !== 1 ? 's' : ''}\n• Total hours: ${connection.totalHours}h\n• Dates: ${dates}\n• Task: ${connection.taskTitle}`;
    }
    
    return baseContent;
  };

  const exportToCSV = () => {
    const csvData = [];
    
    // Header row
    const header = ['Date', ...selectedUsers.map(userId => {
      const userData = timeCapsuleData[userId];
      return userData?.user?.display_name || 'Unknown User';
    })];
    csvData.push(header.join(','));
    
    // Data rows
    uniqueDates.forEach(date => {
      const row = [formatDateForDisplay(date)];
      
      selectedUsers.forEach(userId => {
        const tasks = getTasksForUserAndDate(userId, date);
        const taskText = tasks.map(task => 
          `${task.tasks?.title || 'Unknown Task'} (${task.hours || 0}h)`
        ).join('; ');
        row.push(`"${taskText}"`);
      });
      
      csvData.push(row.join(','));
    });
    
    const csvContent = csvData.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-capsule-${dateRange.startDate}-${dateRange.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getMultiDayStats = () => {
    const totalMultiDayTasks = Object.keys(taskConnections).length;
    const totalInstances = Object.values(taskConnections).reduce((sum, conn) => sum + conn.instances.length, 0);
    const totalHours = Object.values(taskConnections).reduce((sum, conn) => sum + conn.totalHours, 0);
    
    // Group by date count to show distribution
    const dateCountDistribution = {};
    Object.values(taskConnections).forEach(conn => {
      const count = conn.dateCount;
      dateCountDistribution[count] = (dateCountDistribution[count] || 0) + 1;
    });
    
    return {
      totalMultiDayTasks,
      totalInstances,
      totalHours,
      dateCountDistribution
    };
  };

  const renderMultiDayStats = () => {
    const stats = getMultiDayStats();
    
    if (stats.totalMultiDayTasks === 0) return null;
    
    return (
      <div className="time-capsule-multi-day-stats">
        <h4>📅 Multi-day Task Summary</h4>
        <div className="time-capsule-stats-grid">
          <div className="time-capsule-stat-item">
            <div className="time-capsule-stat-number">{stats.totalMultiDayTasks}</div>
            <div className="time-capsule-stat-label">Task Groups</div>
          </div>
          <div className="time-capsule-stat-item">
            <div className="time-capsule-stat-number">{stats.totalInstances}</div>
            <div className="time-capsule-stat-label">Total Entries</div>
          </div>
          <div className="time-capsule-stat-item">
            <div className="time-capsule-stat-number">{stats.totalHours}h</div>
            <div className="time-capsule-stat-label">Total Hours</div>
          </div>
        </div>
        
        {Object.keys(stats.dateCountDistribution).length > 1 && (
          <div className="time-capsule-distribution-info">
            <div className="time-capsule-distribution-title">Distribution:</div>
            <div className="time-capsule-distribution-items">
              {Object.entries(stats.dateCountDistribution)
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .map(([days, count]) => (
                  <span key={days} className="time-capsule-distribution-item">
                    {count} task{count !== 1 ? 's' : ''} spanning {days} day{days !== '1' ? 's' : ''}
                  </span>
                ))}
            </div>
          </div>
        )}
        
        <div className="time-capsule-stats-hint">
          💡 Hover over any multi-day task to see all related entries highlighted
        </div>
      </div>
    );
  };

  return (
    <div className="time-capsule-container">
      <div className="time-capsule-header">
        <div className="time-capsule-header-content">
          <h1>Time Capsule</h1>
          <p>Detailed retrospective of team work plans with task continuity</p>
        </div>
        
        <div className="time-capsule-header-actions">
          <Button 
            onClick={fetchTimeCapsuleData}
            disabled={loading || selectedUsers.length === 0}
            className="time-capsule-refresh-button"
          >
            <FeatherRefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          
          <Button 
            onClick={exportToCSV}
            disabled={Object.keys(timeCapsuleData).length === 0}
            className="time-capsule-export-button"
          >
            <FeatherDownload className="w-4 h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="time-capsule-controls">
        {/* Date Range Selection */}
        <div className="time-capsule-control-section">
          <div className="time-capsule-control-label">
            <FeatherCalendar className="w-4 h-4" />
            Date Range
          </div>
          <div className="time-capsule-date-range-inputs">
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="time-capsule-date-input"
            />
            <span className="time-capsule-date-separator">to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="time-capsule-date-input"
            />
          </div>
        </div>

        {/* User Selection */}
        <div className="time-capsule-control-section">
          <div className="time-capsule-control-label">
            <FeatherUsers className="w-4 h-4" />
            Team Members
          </div>
          <div className="time-capsule-user-selection">
            <Button 
              onClick={handleSelectAllUsers}
              className="time-capsule-select-all-button"
            >
              {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
            </Button>
            
            <div className="time-capsule-user-checkboxes">
              {users.map(user => (
                <label key={user.id} className="time-capsule-user-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => handleUserSelection(user.id)}
                  />
                  <span className="time-capsule-user-name">{user.display_name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Task Continuity Stats */}
        {Object.keys(taskConnections).length > 0 && (
          <div className="time-capsule-control-section">
            <div className="time-capsule-control-label">
              <div className="time-capsule-task-continuity-icon">📅</div>
              Task Continuity
            </div>
            {renderMultiDayStats()}
          </div>
        )}
      </div>

      {/* Time Capsule Table */}
      {loading ? (
        <div className="time-capsule-loading-container">
          <div className="time-capsule-loading-spinner"></div>
          <p>Loading time capsule data...</p>
        </div>
      ) : Object.keys(timeCapsuleData).length > 0 && uniqueDates.length > 0 ? (
        <div className="time-capsule-table-container">
          <table className="time-capsule-table">
            <thead>
              <tr>
                <th className="time-capsule-date-header">Date</th>
                {selectedUsers.map(userId => {
                  const userData = timeCapsuleData[userId];
                  return (
                    <th key={userId} className="time-capsule-user-header">
                      {userData?.user?.display_name || 'Unknown User'}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {uniqueDates.map(date => (
                <tr key={date} className="time-capsule-date-row">
                  <td className="time-capsule-date-cell">
                    {formatDateForDisplay(date)}
                  </td>
                  {selectedUsers.map(userId => {
                    const tasks = getTasksForUserAndDate(userId, date);
                    return (
                      <td key={userId} className="time-capsule-user-cell">
                        {tasks.length > 0 ? (
                          <div className="time-capsule-tasks-container">
                            {tasks.map(task => (
                              <Tooltip
                                key={task.id}
                                title={task.tasks?.title || 'Unknown Task'}
                                description={getTaskTooltipContent(task)}
                                placement="top"
                                trigger="click"
                              >
                                <div 
                                  className={`time-capsule-task-card ${isTaskConnected(task.id) ? 'connected' : ''} ${isTaskHighlighted(task.id) ? 'highlighted' : ''}`}
                                  onMouseEnter={() => handleTaskHover(task.id)}
                                  onMouseLeave={handleTaskLeave}
                                >
                                  <div className="time-capsule-task-content">
                                    <div className="time-capsule-task-title">
                                      {task.tasks?.title || 'No Title'}
                                    </div>
                                    <div className="time-capsule-task-details">
                                      <span className="time-capsule-task-hours">{task.hours || 0}h</span>
                                      <span className={`time-capsule-task-status ${task.status?.toLowerCase() || 'pending'}`}>
                                        {task.status || 'Pending'}
                                      </span>
                                    </div>
                                  </div>
                                  {isTaskConnected(task.id) && (
                                    <>
                                      {/* <div className="time-capsule-continuity-badge">
                                        Multi-day
                                      </div> */}
                                      <div className="time-capsule-day-marker">
                                        {getTaskDayNumber(task.id)}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </Tooltip>
                            ))}
                          </div>
                        ) : (
                          <div className="time-capsule-no-tasks">No tasks</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : selectedUsers.length > 0 ? (
        <div className="time-capsule-no-data">
          <p>No work plan data found for the selected date range and users.</p>
        </div>
      ) : (
        <div className="time-capsule-no-selection">
          <p>Please select a date range and at least one team member to view the time capsule.</p>
        </div>
      )}
    </div>
  );
} 